import { Badge, Box, Flex, Spinner, Text } from '@chakra-ui/react'
import React from 'react'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { KnowledgeSource } from '../../types/chatTypes'

interface ProgressIndicatorProps {
  type: 'external' | 'internal'
  knowledgeSource?: KnowledgeSource
  isActive: boolean
  hasError?: boolean
  onRetry?: () => void
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  type,
  knowledgeSource,
  isActive,
  hasError,
  onRetry
}) => {
  const { t } = useLanguage()

  const getProgressText = () => {
    if (hasError) {
      return type === 'internal' ? t('common.internalError') : 'Search error'
    }
    return type === 'internal' ? t('common.progressInternal') : t('common.progressExternal')
  }

  const getBadgeColor = () => {
    if (hasError) return 'red'
    if (isActive) return type === 'internal' ? 'blue' : 'gray'
    return 'green'
  }

  const getSourceLabel = () => {
    if (type === 'internal') return t('common.internalGuidelines')
    return t('common.generalInformation')
  }

  if (!isActive && !hasError) return null

  return (
    <Flex align="center" gap={2} py={1}>
      <Badge
        colorScheme={getBadgeColor()}
        variant="subtle"
        fontSize="xs"
        px={2}
        py={1}
        borderRadius="full"
      >
        {getSourceLabel()}
      </Badge>

      {isActive && !hasError && (
        <>
          <Spinner size="xs" color={type === 'internal' ? 'blue.500' : 'gray.500'} />
          <Text fontSize="xs" color="gray.600" fontStyle="italic">
            {getProgressText()}
          </Text>
        </>
      )}

      {hasError && (
        <Flex align="center" gap={2}>
          <Text fontSize="xs" color="red.600" fontStyle="italic">
            {getProgressText()}
          </Text>
          {onRetry && (
            <Box
              as="button"
              fontSize="xs"
              color="red.600"
              textDecoration="underline"
              cursor="pointer"
              onClick={onRetry}
              _hover={{ color: 'red.700' }}
              _focus={{ outline: '2px solid', outlineColor: 'red.500', outlineOffset: '1px' }}
            >
              {t('common.tryAgain')}
            </Box>
          )}
        </Flex>
      )}
    </Flex>
  )
}
